import { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { getApiToken } from '../../../utils/authUtils';
import notify from 'devextreme/ui/notify';
import { 
    saveStopwatchState, 
    loadStopwatchState, 
    hasStopwatchState,
    loadStopwatchConfig,
    loadSavedSessions,
    updateNamedSession
} from '../persistence';

export const useStopwatchState = () => {
    const { instance, accounts } = useMsal();
    
    // Grid und UI State
    const [lanes, setLanes] = useState(3);
    const [rows, setRows] = useState(4);
    const [grid, setGrid] = useState([]);
    const [isSetupMode, setIsSetupMode] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    
    // Timer State
    const [activeStopwatches, setActiveStopwatches] = useState({});
    const [recordedTimes, setRecordedTimes] = useState({});
    const [currentTimes, setCurrentTimes] = useState({});
    const [lapTimes, setLapTimes] = useState({});
    const [startTimes, setStartTimes] = useState({});
    const [lapStartTimes, setLapStartTimes] = useState({});
    const [unknownTimes, setUnknownTimes] = useState([]);
    
    // Sportler und UI State
    const [athleteData, setAthleteData] = useState([]);
    const [selectedCell, setSelectedCell] = useState(null);
    const [swapMode, setSwapMode] = useState(false);
    const [firstSwapCell, setFirstSwapCell] = useState(null);
    
    // Session State
    const [sessionName, setSessionName] = useState('');
    const [savedSessions, setSavedSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    
    // Popup State
    const [showAthleteSelector, setShowAthleteSelector] = useState(false);
    const [showUnknownTimePopup, setShowUnknownTimePopup] = useState(false);
    const [showExportPopup, setShowExportPopup] = useState(false);
    const [showSaveSessionDialog, setShowSaveSessionDialog] = useState(false);
    const [showLoadSessionDialog, setShowLoadSessionDialog] = useState(false);
    
    // Refs
    const autoSaveIntervalRef = useRef(null);

    // Lade Sportler-Daten
    const loadAthletes = useCallback(async () => {
        try {
            const token = await getApiToken(instance, accounts);
            const dataSource = createStore({
                key: 'athleteId',
                loadUrl: `${process.env.REACT_APP_serverEndpoint}/Athlete/Athletes`,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = {
                        'Authorization': "Bearer " + token
                    };
                },
            });
            
            const athletes = await dataSource.load({ filter: ['status', '=', 'aktiv'] });
            setAthleteData(athletes);
        } catch (error) {
            console.error('Fehler beim Laden der Sportler:', error);
            notify('Fehler beim Laden der Sportler', 'error');
        }
    }, [instance, accounts]);

    // Auto-Save Funktion
    const saveCurrentState = useCallback(() => {
        if (!isSetupMode && autoSaveEnabled) {
            const now = Date.now();
            const updatedCurrentTimes = { ...currentTimes };
            const updatedLapTimes = { ...lapTimes };
            const updatedStartTimes = { ...startTimes };
            const updatedLapStartTimes = { ...lapStartTimes };

            // Aktualisiere Zeiten für aktive Timer
            Object.keys(activeStopwatches).forEach(key => {
                if (activeStopwatches[key]) {
                    if (startTimes[key]) {
                        updatedCurrentTimes[key] = now - startTimes[key];
                    }
                    if (lapStartTimes[key]) {
                        updatedLapTimes[key] = now - lapStartTimes[key];
                    }
                }
            });

            const state = {
                lanes,
                rows,
                grid,
                activeStopwatches,
                recordedTimes,
                currentTimes: updatedCurrentTimes,
                lapTimes: updatedLapTimes,
                startTimes: updatedStartTimes,
                lapStartTimes: updatedLapStartTimes,
                unknownTimes,
                isSetupMode
            };
            
            // Speichere temporären Zustand
            saveStopwatchState(state);
            
            // Wenn eine benannte Sitzung aktiv ist, aktualisiere auch diese
            if (currentSessionId) {
                const sessionState = {
                    lanes,
                    rows,
                    grid,
                    activeStopwatches,
                    recordedTimes,
                    currentTimes: updatedCurrentTimes,
                    lapTimes: updatedLapTimes,
                    startTimes: updatedStartTimes,
                    lapStartTimes: updatedLapStartTimes,
                    unknownTimes,
                    isSetupMode: false
                };
                updateNamedSession(currentSessionId, sessionState);
            }
        }
    }, [lanes, rows, grid, activeStopwatches, recordedTimes, currentTimes, lapTimes, startTimes, lapStartTimes, unknownTimes, isSetupMode, autoSaveEnabled, currentSessionId]);

    // Lade gespeicherten Zustand beim Initialisieren
    useEffect(() => {
                setIsLoading(false);
        loadAthletes();
    }, [loadAthletes]);

    // Auto-Save Setup
    useEffect(() => {
        if (autoSaveEnabled && !isSetupMode) {
            autoSaveIntervalRef.current = setInterval(saveCurrentState, 5000);
            
            return () => {
                if (autoSaveIntervalRef.current) {
                    clearInterval(autoSaveIntervalRef.current);
                }
            };
        }
    }, [autoSaveEnabled, isSetupMode, saveCurrentState]);

    // Speichere bei wichtigen Änderungen sofort
    useEffect(() => {
        if (!isLoading && !isSetupMode) {
            saveCurrentState();
        }
    }, [grid, recordedTimes, unknownTimes, saveCurrentState, isLoading, isSetupMode]);

    // Speichere vor dem Schließen der Seite
    useEffect(() => {
        const handleBeforeUnload = () => {
            saveCurrentState();
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                saveCurrentState();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (autoSaveIntervalRef.current) {
                clearInterval(autoSaveIntervalRef.current);
            }
        };
    }, [saveCurrentState]);

    // Lade Sessions
    const loadSessions = useCallback(() => {
        const sessions = loadSavedSessions();
        setSavedSessions(sessions);
    }, []);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    return {
        // State
        lanes, setLanes,
        rows, setRows,
        grid, setGrid,
        isSetupMode, setIsSetupMode,
        isLoading, setIsLoading,
        activeStopwatches, setActiveStopwatches,
        recordedTimes, setRecordedTimes,
        currentTimes, setCurrentTimes,
        lapTimes, setLapTimes,
        startTimes, setStartTimes,
        lapStartTimes, setLapStartTimes,
        unknownTimes, setUnknownTimes,
        athleteData, setAthleteData,
        selectedCell, setSelectedCell,
        swapMode, setSwapMode,
        firstSwapCell, setFirstSwapCell,
        sessionName, setSessionName,
        savedSessions, setSavedSessions,
        currentSessionId, setCurrentSessionId,
        autoSaveEnabled, setAutoSaveEnabled,
        showAthleteSelector, setShowAthleteSelector,
        showUnknownTimePopup, setShowUnknownTimePopup,
        showExportPopup, setShowExportPopup,
        showSaveSessionDialog, setShowSaveSessionDialog,
        showLoadSessionDialog, setShowLoadSessionDialog,
        
        // Functions
        saveCurrentState,
        loadSessions
    };
};
